<template>
  <Dialog
    :visible="visible"
    modal
    header="Маппинг брендов (авто-определение при импорте)"
    :style="{ width: '70vw', height: '80vh' }"
    :maximizable="true"
    @update:visible="$emit('update:visible', $event)"
  >
    <div class="container">
      <div class="flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
        <p class="text-600 mt-1 mb-0" style="max-width: 70%">
          <b>alias</b> — текст бренда → канонический (POLO → POLO RALPH LAUREN).
          <b>prefix</b> — префикс кода модели → бренд внутри семейства
          (PH/PP → POLO RALPH LAUREN, RA → RALPH, RL → RALPH LAUREN).
          Префикс модели главнее текста бренда.
        </p>
        <Button label="Добавить правило" icon="pi pi-plus" @click="openCreate" />
      </div>

      <DataTable
        :value="rules"
        :loading="isLoading"
        :paginator="rules.length > 15"
        :rows="15"
        class="p-datatable-sm"
        emptyMessage="Правил пока нет"
      >
        <Column field="family" header="Семейство" style="min-width: 160px" />
        <Column header="Тип" style="width: 110px">
          <template #body="{ data }">
            <Tag
              :value="data.rule_type"
              :severity="data.rule_type === 'prefix' ? 'info' : 'success'"
            />
          </template>
        </Column>
        <Column field="source" header="Источник" style="min-width: 140px" />
        <Column field="canonical_brand" header="Канонический бренд" style="min-width: 200px" />
        <Column field="notes" header="Заметка" style="min-width: 140px" />
        <Column header="" style="width: 110px">
          <template #body="{ data }">
            <Button icon="pi pi-pencil" text rounded @click="openEdit(data)" />
            <Button icon="pi pi-trash" text rounded severity="danger" @click="remove(data)" />
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Вложенный диалог создания/редактирования -->
    <Dialog
      v-model:visible="editVisible"
      modal
      :header="form.id ? 'Редактировать правило' : 'Новое правило'"
      :style="{ width: '420px' }"
    >
      <div class="flex flex-column gap-3 pt-2">
        <div class="flex flex-column gap-1">
          <label>Тип правила</label>
          <Dropdown
            v-model="form.rule_type"
            :options="ruleTypes"
            optionLabel="label"
            optionValue="value"
          />
        </div>
        <div class="flex flex-column gap-1">
          <label>{{ form.rule_type === 'prefix' ? 'Префикс кода модели' : 'Текст бренда' }}</label>
          <InputText v-model="form.source" placeholder="напр. POLO или PH" />
        </div>
        <div class="flex flex-column gap-1">
          <label>Канонический бренд</label>
          <InputText v-model="form.canonical_brand" placeholder="POLO RALPH LAUREN" />
        </div>
        <div class="flex flex-column gap-1">
          <label>Семейство</label>
          <InputText v-model="form.family" placeholder="RALPH LAUREN" />
        </div>
        <div class="flex flex-column gap-1">
          <label>Заметка (опц.)</label>
          <InputText v-model="form.notes" />
        </div>
      </div>
      <template #footer>
        <Button label="Отмена" text @click="editVisible = false" />
        <Button label="Сохранить" :loading="isSaving" @click="save" />
      </template>
    </Dialog>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import apiService from '../services/apiService'

export default {
  name: 'BrandMappingDialog',
  components: { Dialog, DataTable, Column, Button, Tag, Dropdown, InputText },
  props: { visible: { type: Boolean, default: false } },
  emits: ['update:visible'],
  data() {
    return {
      rules: [],
      isLoading: false,
      isSaving: false,
      editVisible: false,
      ruleTypes: [
        { label: 'alias (текст бренда)', value: 'alias' },
        { label: 'prefix (код модели)', value: 'prefix' },
      ],
      form: this.emptyForm(),
    }
  },
  watch: {
    visible(open) {
      if (open) this.load()
    },
  },
  methods: {
    emptyForm() {
      return { id: null, rule_type: 'alias', source: '', canonical_brand: '', family: '', notes: '' }
    },
    async load() {
      this.isLoading = true
      try {
        this.rules = await apiService.getBrandMappingRules()
      } catch (e) {
        this.$toast?.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось загрузить правила', life: 4000 })
      } finally {
        this.isLoading = false
      }
    },
    openCreate() {
      this.form = this.emptyForm()
      this.editVisible = true
    },
    openEdit(rule) {
      this.form = {
        id: rule.id, rule_type: rule.rule_type, source: rule.source,
        canonical_brand: rule.canonical_brand, family: rule.family, notes: rule.notes || '',
      }
      this.editVisible = true
    },
    async save() {
      const payload = {
        rule_type: this.form.rule_type,
        source: this.form.source.trim(),
        canonical_brand: this.form.canonical_brand.trim(),
        family: this.form.family.trim(),
        notes: this.form.notes?.trim() || null,
      }
      if (!payload.source || !payload.canonical_brand || !payload.family) {
        this.$toast?.add({ severity: 'warn', summary: 'Проверьте поля', detail: 'Источник, бренд и семейство обязательны', life: 4000 })
        return
      }
      this.isSaving = true
      try {
        if (this.form.id) {
          await apiService.updateBrandMappingRule(this.form.id, payload)
        } else {
          await apiService.createBrandMappingRule(payload)
        }
        this.editVisible = false
        await this.load()
      } catch (e) {
        const detail = e?.response?.data?.detail || 'Не удалось сохранить правило'
        this.$toast?.add({ severity: 'error', summary: 'Ошибка', detail, life: 5000 })
      } finally {
        this.isSaving = false
      }
    },
    async remove(rule) {
      if (!window.confirm(`Удалить правило ${rule.rule_type} «${rule.source}»?`)) return
      try {
        await apiService.deleteBrandMappingRule(rule.id)
        await this.load()
      } catch (e) {
        this.$toast?.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось удалить', life: 4000 })
      }
    },
  },
}
</script>
